import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Pencil, Plus, Shield, Trash2, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/services/api';
import { type AdminRole, type PermissionDefinition } from '@/types';
import { formatDate } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface ManagedUser {
  id: string;
  username: string;
  email?: string | null;
  displayName?: string | null;
  enabled?: boolean;
  roleItems?: Array<{ id: string; name: string }>;
  permissions?: string[];
  createdAt: string;
}

type UserFormState = {
  username: string;
  email: string;
  displayName: string;
  password: string;
  roleIds: string[];
  enabled: boolean;
};

type RoleFormState = {
  id?: string;
  name: string;
  description: string;
  keycloakRoleName: string;
  permissionKeys: string[];
};

const emptyUserForm: UserFormState = {
  username: '',
  email: '',
  displayName: '',
  password: '',
  roleIds: [],
  enabled: true,
};

const emptyRoleForm: RoleFormState = {
  name: '',
  description: '',
  keycloakRoleName: '',
  permissionKeys: [],
};

export function UsersPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [userForm, setUserForm] = useState<UserFormState>(emptyUserForm);
  const [roleForm, setRoleForm] = useState<RoleFormState>(emptyRoleForm);

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers() as Promise<ManagedUser[]>,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.getRoles(),
  });

  const { data: permissions = [] } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.getPermissions(),
  });

  const createUserMutation = useMutation({
    mutationFn: (form: UserFormState) =>
      api.createUser({
        username: form.username,
        email: form.email,
        displayName: form.displayName || undefined,
        password: form.password,
        roles: [],
        roleIds: form.roleIds,
        enabled: form.enabled,
      }),
    onSuccess: () => {
      toast({ title: 'Benutzer erstellt' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsUserDialogOpen(false);
      setEditingUser(null);
      setUserForm(emptyUserForm);
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: UserFormState }) =>
      api.updateUser(id, {
        email: form.email,
        displayName: form.displayName || undefined,
        roleIds: form.roleIds,
        isActive: form.enabled,
      }),
    onSuccess: () => {
      toast({ title: 'Benutzer aktualisiert' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsUserDialogOpen(false);
      setEditingUser(null);
      setUserForm(emptyUserForm);
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      toast({ title: 'Benutzer deaktiviert' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    },
  });

  const upsertRoleMutation = useMutation({
    mutationFn: (form: RoleFormState) => {
      if (form.id) {
        return api.updateRole(form.id, {
          name: form.name,
          description: form.description || undefined,
          keycloakRoleName: form.keycloakRoleName || undefined,
          permissionKeys: form.permissionKeys,
        });
      }
      return api.createRole({
        name: form.name,
        description: form.description || undefined,
        keycloakRoleName: form.keycloakRoleName || undefined,
        permissionKeys: form.permissionKeys,
      });
    },
    onSuccess: () => {
      toast({ title: 'Rolle gespeichert' });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsRoleDialogOpen(false);
      setEditingRole(null);
      setRoleForm(emptyRoleForm);
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => api.deleteRole(id),
    onSuccess: () => {
      toast({ title: 'Rolle gelöscht' });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    },
  });

  const groupedPermissions = useMemo(() => {
    return permissions.reduce<Record<string, PermissionDefinition[]>>((acc, permission) => {
      if (!acc[permission.category]) {
        acc[permission.category] = [];
      }
      acc[permission.category].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.displayName || ''} ${user.username} ${user.email || ''}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm(emptyUserForm);
    setIsUserDialogOpen(true);
  };

  const openEditUser = (user: ManagedUser) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email || '',
      displayName: user.displayName || '',
      password: '',
      roleIds: user.roleItems?.map((role) => role.id) || [],
      enabled: user.enabled !== false,
    });
    setIsUserDialogOpen(true);
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm(emptyRoleForm);
    setIsRoleDialogOpen(true);
  };

  const openEditRole = (role: AdminRole) => {
    setEditingRole(role);
    setRoleForm({
      id: role.id,
      name: role.name,
      description: role.description || '',
      keycloakRoleName: role.keycloakRoleName || '',
      permissionKeys: role.permissionKeys,
    });
    setIsRoleDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Lädt Benutzer & Rollen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="text-destructive">Fehler beim Laden der Benutzerverwaltung</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Benutzer & Rollen</h1>
          <p className="text-muted-foreground">
            Benutzerkonten, Rollen und granular zugeordnete Berechtigungen verwalten.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openCreateRole}>
            <Shield className="mr-2 h-4 w-4" />
            Neue Rolle
          </Button>
          <Button onClick={openCreateUser}>
            <Plus className="mr-2 h-4 w-4" />
            Neuer Benutzer
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5" />
            Rollen ({roles.length})
          </CardTitle>
          <CardDescription>Rollen bündeln Permission-Keys für Menüs, Views und Aktionen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {roles.map((role) => (
            <div key={role.id} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium">{role.name}</p>
                  <p className="text-sm text-muted-foreground">{role.description || 'Keine Beschreibung'}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Nutzer: {role.userCount} • Keycloak: {role.keycloakRoleName || '-'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEditRole(role)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={role.system}
                    onClick={() => deleteRoleMutation.mutate(role.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {role.permissionKeys.slice(0, 8).map((permission) => (
                  <Badge key={permission} variant="secondary" className="text-xs">
                    {permission}
                  </Badge>
                ))}
                {role.permissionKeys.length > 8 && (
                  <Badge variant="outline" className="text-xs">
                    +{role.permissionKeys.length - 8} weitere
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Benutzer ({filteredUsers.length})
            </CardTitle>
            <Input
              className="w-64"
              placeholder="Benutzer suchen..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2">Benutzer</th>
                  <th className="pb-2">Rollen</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Erstellt</th>
                  <th className="pb-2 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b last:border-0">
                    <td className="py-3">
                      <p className="font-medium">{user.displayName || user.username}</p>
                      <p className="text-xs text-muted-foreground">
                        @{user.username} {user.email ? `• ${user.email}` : ''}
                      </p>
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-1">
                        {user.roleItems?.map((role) => (
                          <Badge key={role.id} variant="secondary" className="text-xs">
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-3">
                      <Badge variant={user.enabled === false ? 'destructive' : 'default'}>
                        {user.enabled === false ? 'Deaktiviert' : 'Aktiv'}
                      </Badge>
                    </td>
                    <td className="py-3 text-muted-foreground">{formatDate(user.createdAt)}</td>
                    <td className="py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditUser(user)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteUserMutation.mutate(user.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Benutzer bearbeiten' : 'Benutzer anlegen'}</DialogTitle>
            <DialogDescription>Rollen werden über IDs zugewiesen und im Backend synchronisiert.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!editingUser && (
              <div className="grid gap-1">
                <Label>Benutzername</Label>
                <Input
                  value={userForm.username}
                  onChange={(event) => setUserForm((prev) => ({ ...prev, username: event.target.value }))}
                />
              </div>
            )}
            <div className="grid gap-1">
              <Label>E-Mail</Label>
              <Input
                value={userForm.email}
                onChange={(event) => setUserForm((prev) => ({ ...prev, email: event.target.value }))}
              />
            </div>
            <div className="grid gap-1">
              <Label>Anzeigename</Label>
              <Input
                value={userForm.displayName}
                onChange={(event) => setUserForm((prev) => ({ ...prev, displayName: event.target.value }))}
              />
            </div>
            {!editingUser && (
              <div className="grid gap-1">
                <Label>Initiales Passwort</Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(event) => setUserForm((prev) => ({ ...prev, password: event.target.value }))}
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label>Rollen</Label>
              <div className="grid gap-2 md:grid-cols-2">
                {roles.map((role) => {
                  const checked = userForm.roleIds.includes(role.id);
                  return (
                    <label key={role.id} className="flex items-start gap-2 rounded border p-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setUserForm((prev) => ({
                            ...prev,
                            roleIds: checked
                              ? prev.roleIds.filter((roleId) => roleId !== role.id)
                              : [...prev.roleIds, role.id],
                          }))
                        }
                      />
                      <span>
                        <span className="block font-medium">{role.name}</span>
                        <span className="text-xs text-muted-foreground">{role.description || '-'}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={userForm.enabled}
                onChange={(event) => setUserForm((prev) => ({ ...prev, enabled: event.target.checked }))}
              />
              Benutzer aktiv
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUserDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() =>
                editingUser
                  ? updateUserMutation.mutate({ id: editingUser.id, form: userForm })
                  : createUserMutation.mutate(userForm)
              }
              disabled={
                !userForm.email ||
                !userForm.roleIds.length ||
                (!editingUser && (!userForm.username || !userForm.password)) ||
                createUserMutation.isPending ||
                updateUserMutation.isPending
              }
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="sm:max-w-[760px]">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Rolle bearbeiten' : 'Rolle erstellen'}</DialogTitle>
            <DialogDescription>Permissions steuern Menüs, Tabs und API-Aktionen granular.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1">
              <Label>Name</Label>
              <Input value={roleForm.name} onChange={(event) => setRoleForm((prev) => ({ ...prev, name: event.target.value }))} />
            </div>
            <div className="grid gap-1">
              <Label>Beschreibung</Label>
              <Input
                value={roleForm.description}
                onChange={(event) => setRoleForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </div>
            <div className="grid gap-1">
              <Label>Keycloak-Rollenname (optional)</Label>
              <Input
                value={roleForm.keycloakRoleName}
                onChange={(event) => setRoleForm((prev) => ({ ...prev, keycloakRoleName: event.target.value }))}
              />
            </div>
            <div className="max-h-72 space-y-3 overflow-y-auto rounded border p-3">
              {Object.entries(groupedPermissions).map(([category, items]) => (
                <div key={category}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{category}</p>
                  <div className="grid gap-2 md:grid-cols-2">
                    {items.map((permission) => {
                      const checked = roleForm.permissionKeys.includes(permission.key);
                      return (
                        <label key={permission.id} className="flex items-start gap-2 rounded border p-2 text-xs">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setRoleForm((prev) => ({
                                ...prev,
                                permissionKeys: checked
                                  ? prev.permissionKeys.filter((key) => key !== permission.key)
                                  : [...prev.permissionKeys, permission.key],
                              }))
                            }
                          />
                          <span>
                            <span className="block font-medium">{permission.key}</span>
                            <span className="text-muted-foreground">{permission.description || '-'}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => upsertRoleMutation.mutate(roleForm)}
              disabled={!roleForm.name || roleForm.permissionKeys.length === 0 || upsertRoleMutation.isPending}
            >
              Rolle speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
